import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'

import {
  Button,
  DateRange,
  Stack,
  Subheader1,
  ToggleButton,
  ToggleButtonGroup,
  useDebouncedValue,
  useDialogState,
  useIntersectionObserver,
  DateRangeProps,
} from '@collaborate-health/uikit'
import { LinearProgress } from '@mui/material'
import { Add, AddLocationAltOutlined, ConnectedTvOutlined, FactCheck } from '@mui/icons-material'

import { useDispatch, useSelector } from '@tabeeb/store'
import { getIsTabExpanded, hasEditCheckInsPermission } from '@tabeeb/modules/sessionTabs/selectors'
import CheckInsList from '@tabeeb/modules/checkIns/components/CheckInsList'
import CheckInsTable from '@tabeeb/modules/checkIns/components/CheckInsTable'
import { useContentCheckIns } from '@tabeeb/modules/checkIns/hooks'
import { resetCheckInsFilterModel } from '@tabeeb/modules/checkIns'
import { TabPanel } from '@tabeeb/enums'
import {
  GetTrackingSnapshotFiltersRequest,
  GetTrackingSnapshotRequest,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { sessionTitleFormatter } from '@tabeeb/shared/utils/text'
import { ContentCheckInsTabViews } from '@tabeeb/modules/checkIns/components/ContentCheckInsTab/constants'
import AddUsersCheckInDialog from '../AddUsersCheckInDialog'
import { RootContainer, ScrollableContainer } from './ContentCheckInsTab.styles'
import { ContentCheckInsTabProps } from './ContentCheckInsTab.types'

const ContentCheckInsTab: React.FC<ContentCheckInsTabProps> = ({ contentId }) => {
  const dispatch = useDispatch()
  const parentScrollRef = useRef<HTMLDivElement | null>(null)
  const [addDialogOpen, onAddDialogOpen, onAddDialogClose] = useDialogState()
  const [currentView, setCurrentView] = useState<ContentCheckInsTabViews>(ContentCheckInsTabViews.CurrentContent)
  const [request, setRequest] = useState<GetTrackingSnapshotRequest>({
    StartDate: moment().toDate().toISOString(),
    EndDate: moment().toDate().toISOString(),
    Skip: 0,
    Take: 10,
    OnReview: currentView === ContentCheckInsTabViews.OnReview,
    Filters: {},
  })

  useEffect(() => {
    return () => {
      dispatch(resetCheckInsFilterModel())
    }
  }, [dispatch])

  const expanded = useSelector<boolean>((state) => getIsTabExpanded(state, TabPanel.CheckIns))

  const {
    isFetching: areCheckInsFetching,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    checkIns,
    totalCount,
  } = useContentCheckIns({ contentId, params: request })
  const debouncedFetching = useDebouncedValue(areCheckInsFetching, 300)

  const onNextPage = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onPageSizeChange = useCallback(async (size: number) => {
    setRequest((prev) => ({ ...prev, Take: size, Skip: 0 }))
  }, [])

  const onDateChange = useCallback<DateRangeProps['onChange']>((value) => {
    setRequest((prev) => ({
      ...prev,
      StartDate: value.start.toISOString(),
      EndDate: value.end.toISOString(),
    }))
  }, [])

  const onFiltersChange = useCallback((val: GetTrackingSnapshotFiltersRequest) => {
    setRequest((prev) => ({
      ...prev,
      Filters: val,
    }))
  }, [])

  const onViewChange = useCallback((_e, val: ContentCheckInsTabViews) => {
    setCurrentView(val)
    setRequest((prev) => ({ ...prev, OnReview: val === ContentCheckInsTabViews.OnReview }))
  }, [])

  const sentinelRef = useIntersectionObserver({
    onIntersect: onNextPage,
    options: { rootMargin: '200px' },
    rootRef: parentScrollRef,
  })

  const showAddButton = useSelector<boolean>(hasEditCheckInsPermission)

  const addRecordButton = useMemo(() => {
    if (expanded) {
      return (
        <Button variant='contained' size='small' color='primary' startIcon={<Add />} onClick={onAddDialogOpen}>
          Add record
        </Button>
      )
    }
    return (
      <Button variant='text' color='primary' startIcon={<AddLocationAltOutlined />} onClick={onAddDialogOpen}>
        Add
      </Button>
    )
  }, [expanded, onAddDialogOpen])

  const viewTypeButton = useMemo(() => {
    if (expanded) {
      return (
        <ToggleButtonGroup value={currentView} onChange={onViewChange} exclusive disabled={areCheckInsFetching}>
          <ToggleButton value={ContentCheckInsTabViews.CurrentContent}>
            {sessionTitleFormatter.format('Current session')}
          </ToggleButton>
          <ToggleButton value={ContentCheckInsTabViews.OnReview}>On review</ToggleButton>
        </ToggleButtonGroup>
      )
    }
    return (
      <ToggleButtonGroup value={currentView} onChange={onViewChange} exclusive disabled={areCheckInsFetching}>
        <ToggleButton value={ContentCheckInsTabViews.CurrentContent}>
          <ConnectedTvOutlined />
        </ToggleButton>
        <ToggleButton value={ContentCheckInsTabViews.OnReview}>
          <FactCheck />
        </ToggleButton>
      </ToggleButtonGroup>
    )
  }, [areCheckInsFetching, currentView, expanded, onViewChange])

  return (
    <RootContainer>
      <Stack direction='row' justifyContent='space-between' alignItems='center' p={1}>
        <Stack direction='row' alignItems='center'>
          <Subheader1 pl={2} noWrap>
            Users check-in
          </Subheader1>
        </Stack>
        <Stack direction='row' spacing={2} alignItems='center' ml={1}>
          {viewTypeButton}
          {showAddButton && addRecordButton}
        </Stack>
      </Stack>
      <DateRange
        value={{ start: new Date(request.StartDate), end: new Date(request.EndDate) }}
        onChange={onDateChange}
        buttonProps={{ maxWidth: 250 }}
      />
      {debouncedFetching && <LinearProgress />}
      {checkIns &&
        (expanded ? (
          <CheckInsTable
            checkIns={checkIns}
            pageSize={request.Take}
            totalCount={totalCount}
            viewType={currentView}
            onNextPage={onNextPage}
            setPageSize={onPageSizeChange}
            setFilters={onFiltersChange}
          />
        ) : (
          <ScrollableContainer ref={parentScrollRef}>
            <CheckInsList
              checkIns={checkIns}
              hasNextPage={hasNextPage || false}
              isFetchingNextPage={isFetchingNextPage}
              viewType={currentView}
              sentinelRef={sentinelRef}
            />
          </ScrollableContainer>
        ))}
      <AddUsersCheckInDialog open={addDialogOpen} onClose={onAddDialogClose} contentId={contentId} />
    </RootContainer>
  )
}

export default ContentCheckInsTab

